<template>
  <div class="property-tab">
    <div class="appearance-grid">
      <span class="appearance-label">Background:</span>
      <div class="appearance-controls">
        <v-menu location="right" :close-on-content-click="false" @update:modelValue="colorPickerHandler">
          <template #activator="{ props: menu }">
            <v-tooltip location="bottom">
              <template #activator="{ props: tooltip }">
                <v-btn class="button-entry-fill-color" size="small" :color="fillColor" v-bind="{ ...menu, ...tooltip }">
                  <v-icon>mdi-format-color-fill</v-icon>
                </v-btn>
              </template>
              <span>Change Background Color</span>
            </v-tooltip>
          </template>
          <v-color-picker v-model="fillColor" class="overflow-hidden" elevation="15" :class="{ disabled: PreferenceStore.INSTANCE.isReadOnly }"></v-color-picker>
        </v-menu>
      </div>
      <span class="appearance-label">Border:</span>
      <div class="appearance-controls">
        <v-menu location="right" :close-on-content-click="false" @update:modelValue="colorPickerHandler">
          <template #activator="{ props: menu }">
            <v-tooltip location="bottom">
              <template #activator="{ props: tooltip }">
                <v-btn class="button-entry-border-color" size="small" :color="borderColor" v-bind="{ ...menu, ...tooltip }">
                  <v-icon>mdi-format-color-fill</v-icon>
                </v-btn>
              </template>
              <span>Change Border Color</span>
            </v-tooltip>
          </template>
          <v-color-picker v-model="borderColor" elevation="15" class="overflow-hidden" :class="{ disabled: PreferenceStore.INSTANCE.isReadOnly }"></v-color-picker>
        </v-menu>
        <v-slider
          v-model="borderWidth"
          class="ml-2"
          hide-details
          min="0" max="10" step="1"
          track-fill-color="primary" track-color="grey" thumb-color="primary"
          :disabled="PreferenceStore.INSTANCE.isReadOnly"
          v-on="{ mousedown: () => startBatchCommand(), mouseup: () => stopBatchCommand() }"
        ></v-slider>
        <span class="slider-value">{{ borderWidth }}</span>
      </div>
    </div>
    <div class="appearance-footer">
      <v-btn size="small" color="primary" :disabled="PreferenceStore.INSTANCE.isReadOnly" @click="resetDefaults()">Reset Defaults</v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import type NamedElement from "../mvc/base/named-element";
import { Entry } from "../mvc/entry";
import { Exit } from "../mvc/exit";
import { computed, inject, onBeforeUnmount, ref } from "vue";
import { PreferenceStore } from "../services/preference-store";
import RappidContext from "../rappid/rappid-context";

const colorPicking = ref<boolean>(false);
const rappidContext: RappidContext = inject<RappidContext>("rappidContext");

const compProps = defineProps<{
  model: NamedElement;
}>();

onBeforeUnmount(() => {
  if (colorPicking.value) {
    rappidContext.commandManager.storeBatchCommand();
  }
});

function colorPickerHandler(value: boolean) {
  colorPicking.value = value;
  if (value) {
    startBatchCommand();
  } else {
    stopBatchCommand();
  }
}

function startBatchCommand() {
  rappidContext.commandManager.initBatchCommand();
}

function stopBatchCommand() {
  rappidContext.commandManager.storeBatchCommand();
}

function resetDefaults() {
  if (compProps.model instanceof Entry) {
    startBatchCommand();
    compProps.model.resetDefaults();
    stopBatchCommand();
  } else if (compProps.model instanceof Exit) {
    startBatchCommand();
    compProps.model.resetDefaults();
    stopBatchCommand();
  }
}

const fillColor = computed({
  get(): string {
    if (compProps.model instanceof Entry) return compProps.model.getFillColor();
    if (compProps.model instanceof Exit) return compProps.model.getFillColor();
    return "";
  },
  set(color: string) {
    if (compProps.model instanceof Entry) compProps.model.setFillColor(color);
    else if (compProps.model instanceof Exit) compProps.model.setFillColor(color);
  },
});

const borderColor = computed({
  get(): string {
    if (compProps.model instanceof Entry) return compProps.model.getBorderColor();
    if (compProps.model instanceof Exit) return compProps.model.getBorderColor();
    return "";
  },
  set(color: string) {
    if (compProps.model instanceof Entry) compProps.model.setBorderColor(color);
    else if (compProps.model instanceof Exit) compProps.model.setBorderColor(color);
  },
});

const borderWidth = computed({
  get(): number {
    if (compProps.model instanceof Entry) return compProps.model.getBorderWidth();
    if (compProps.model instanceof Exit) return compProps.model.getBorderWidth();
    return 0;
  },
  set(width: number) {
    if (compProps.model instanceof Entry) compProps.model.setBorderWidth(width);
    else if (compProps.model instanceof Exit) compProps.model.setBorderWidth(width);
  },
});
</script>
